// lib/distance.js — patch #62: rijafstand via OSRM-cache
// Haversine blijft als fallback. Rijafstanden komen van /api/drive-times.

// ── Haversine (hemelsbreed, km) ────────────────────────────────
export function distanceKm(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371, r = d => d * Math.PI / 180;
  const dLat = r(b[0]-a[0]), dLon = r(b[1]-a[1]);
  const s = Math.sin(dLat/2)**2 + Math.sin(dLon/2)**2 * Math.cos(r(a[0])) * Math.cos(r(b[0]));
  return 2 * R * Math.asin(Math.sqrt(s));
}

// ── Rijafstand-cache (geladen van /api/drive-times) ────────────
let _cache = {};
let _loaded = false;

export async function loadDriveTimes() {
  if (_loaded) return;
  try {
    const res = await fetch("/api/drive-times");
    if (res.ok) { _cache = await res.json(); _loaded = true; }
  } catch { /* silent — val terug op haversine */ }
}

export function getDriveMinutes(townSlug, schoolId) {
  if (!townSlug || schoolId == null) return null;
  return _cache[`${townSlug}:${schoolId}`] ?? null;
}

// ── Dichtstbijzijnde school — sorteer op rijminuten indien beschikbaar ─
export function nearestSchool(townCoords, townSlug, schools, type) {
  if (!townCoords || !schools?.length) return null;
  const candidates = schools.filter(s => s.type === type && s.lat != null && s.lon != null);
  if (!candidates.length) return null;
  let best = null;
  for (const s of candidates) {
    const km       = distanceKm(townCoords, [s.lat, s.lon]);
    const driveMin = getDriveMinutes(townSlug, s.id);
    // Vergelijk op rijminuten; anders geschatte rijminuten op basis van km
    const sort = driveMin ?? Math.round(km * 1.4 / 50 * 60);
    if (!best || sort < best.sort) best = { school: s, km, driveMin, sort };
  }
  return best;  // { school, km, driveMin, sort }
}

// ── Scores ─────────────────────────────────────────────────────
// Rijafstand in minuten → score 0-5
export function driveTimeToScore(minutes) {
  if (minutes == null || !isFinite(minutes)) return 0;
  if (minutes <= 15) return 5;
  if (minutes <= 25) return 4;
  if (minutes <= 40) return 3;
  if (minutes <= 60) return 2;
  if (minutes <= 90) return 1;
  return 0;
}

// Hemelsbreed km → score (fallback voor als rijafstand onbekend)
export function distanceToScore(km) {
  if (km == null || !isFinite(km)) return 0;
  if (km <= 15) return 5;
  if (km <= 30) return 4;
  if (km <= 45) return 3;
  if (km <= 60) return 2;
  if (km <= 100) return 1;
  return 0;
}

// ── Display-formaat ────────────────────────────────────────────
export function formatDriveTime(driveMin, km) {
  if (driveMin != null && isFinite(driveMin)) return `${driveMin} min rijden`;
  if (km != null && isFinite(km) && km < Infinity) {
    const est = Math.round(km * 1.4 / 50 * 60);
    return `~${est} min (schatting)`;
  }
  return "—";
}

export function formatDistanceKm(km) {
  if (km == null || !isFinite(km)) return "—";
  if (km < 1)  return `${Math.round(km * 1000)} m`;
  if (km < 10) return `${km.toFixed(1).replace(".", ",")} km`;
  return `${Math.round(km)} km`;
}

// ── Alle alternatieve scholen binnen maxKm, gesorteerd op afstand ──
// Types: montessori | forest-school | waldorf | alt-school
export const ALT_SCHOOL_TYPES = new Set(["montessori", "forest-school", "waldorf", "alt-school"]);

export function nearbySchools(townCoords, schools, maxKm = 80) {
  if (!townCoords || !schools?.length) return [];
  return schools
    .filter(s => ALT_SCHOOL_TYPES.has(s.type) && s.lat != null && s.lon != null)
    .map(s => ({ school: s, km: distanceKm(townCoords, [s.lat, s.lon]) }))
    .filter(e => e.km <= maxKm)
    .sort((a, b) => a.km - b.km);
}
